import './QualitativeChart.css';

import React from 'react';
import { calculatePaddedDates } from '../helper';
import B2BChart from '../B2BChart';
import { renderArrowItem, renderLastSampleItem, renderObjectiveItem, renderObjectiveTooltip, renderSampleItem, renderSampleTooltip } from './helper';
import BooleanIconToggle from '../../../../b2b-common/BooleanIconToggle';
import { Visibility, VisibilityOff } from '@mui/icons-material';

const wrapText = (text, maxLength) => {
    const words = text.split(' ');
    let wrappedText = '';
    let line = '';

    words.forEach(word => {
        if ((line + word).length > maxLength) {
            wrappedText += line + '\n';
            line = word + ' ';
        } else {
            line += word + ' ';
        }
    });

    wrappedText += line.trim();
    return wrappedText;
};

const getNextSample = (samples, currentSample) => {
    // Sort the samples by date
    const sortedSamples = samples.slice().sort((a, b) => new Date(a.sampleDate) - new Date(b.sampleDate));
    // Find the index of the current sample
    const currentIndex = sortedSamples.findIndex(sample => sample.sampleId === currentSample.sampleId);
    // Return the next sample if it exists, otherwise return null
    return currentIndex >= 0 && currentIndex < sortedSamples.length - 1 ? sortedSamples[currentIndex + 1] : null;
};

const QualitativeChart = ({ indicatorDTO, objective }) => {

    const paddingPercentage = 0.05; // 5% padding
    const { paddedStart, paddedEnd } = calculatePaddedDates(objective.startDate, objective.endDate, paddingPercentage);

    const [showY_axis, setShowY_axis] = React.useState(false);

    const categories = indicatorDTO.qualitativeTypeDTO.weightedValues.map(value => wrapText(value.label, 18));

    const sortedSamples = objective.samplesPerObjective.slice().sort((a, b) => new Date(a.sampleDate) - new Date(b.sampleDate));
    const data = sortedSamples.map((sample, index) => {
        const weightedValue = indicatorDTO.qualitativeTypeDTO.weightedValues.find(
            (value) => value.qualitativeWeightedValueId === sample.sampleValue.qualitativeWeightedValueId
        );
        const nextSample = getNextSample(sortedSamples, sample);
        const nextCategoryId = nextSample ? nextSample.sampleValue.qualitativeWeightedValueId : null;
        const nextCategory = nextCategoryId ? indicatorDTO.qualitativeTypeDTO.weightedValues.find(value => value.qualitativeWeightedValueId === nextCategoryId) : null;

        const endTime = nextSample ? new Date(nextSample.sampleDate).getTime() : new Date(sample.sampleDate).getTime() + 86400000;

        return {
            name: `Task ${index + 1}`,
            isSample: true,
            value: [
                categories.indexOf(wrapText(weightedValue.label, 18)), // careful!! to match with the setup of the yAxis
                new Date(sample.sampleDate).getTime(),
                endTime,
                nextCategory ? categories.indexOf(wrapText(nextCategory.label, 18)) : null,
                sample.status,
                sample.trend,
            ],
        };
    });

    // end time of the last sample will be today's date, the missed date or the end date of the objective
    // whatever comes first
    const today = new Date();
    const objectiveEndDate = new Date(objective.endDate);
    const sampleMissedDate = objective.missedExpectedSampleDate ? new Date(objective.missedExpectedSampleDate) : new Date();
    const lastSampleData = data.length === 0 ? null : {
        name: data[data.length - 1].name,
        isSample: true,
        value: [
            data[data.length - 1].value[0],
            data[data.length - 1].value[1],
            Math.min(today.getTime(), objectiveEndDate.getTime(), sampleMissedDate.getTime()),
            null,
            data[data.length - 1].value[4],
            data[data.length - 1].value[4],
        ],
    }

    const missedDate = objective.missedExpectedSampleDate ? new Date(objective.missedExpectedSampleDate).getTime() : null;
    const missedDateData = missedDate ? {
        name: 'Missed Date',
        isMissedDate: true,
        data: [
            {
                value: [missedDate, 0],
                label: {
                    show: true,
                    formatter: 'Missed Date',
                },
            },
            {
                value: [missedDate, categories.length - 1],
            },
        ],
    } : null;

    const objectivesData = {
        name: 'Objectives',
        isObjective: true,
        value: [
            categories.indexOf(wrapText(objective.startObjectiveValue.qualitativeValue, 18)),
            new Date(objective.startDate).getTime(),
            categories.indexOf(wrapText(objective.endObjectiveValue.qualitativeValue, 18)),
            new Date(objective.endDate).getTime(),
        ],
    };

    const options = {
        grid: {
            top: '5%',
            left: showY_axis ? '20%' : '2%',
            right: '5%',
            bottom: '7%'
        },
        tooltip: {
            trigger: 'item',
            borderWidth: 0,
            padding: 0,
            extraCssText: 'box-shadow: 0 0 0px rgba(0, 0, 0, 0.5);',
            formatter: function (params) {
                if (params.data.isSample) {
                    const sampleValue = indicatorDTO.qualitativeTypeDTO.weightedValues[params.value[0]].label;
                    return renderSampleTooltip(new Date(params.value[1]).toLocaleDateString(), sampleValue, params.value[4], params.value[5]);
                } else if (params.data.isObjective) {
                    const startValue = indicatorDTO.qualitativeTypeDTO.weightedValues[params.value[0]].label;
                    const endValue = indicatorDTO.qualitativeTypeDTO.weightedValues[params.value[2]].label;
                    return renderObjectiveTooltip(new Date(params.value[1]).toLocaleDateString(), startValue, new Date(params.value[3]).toLocaleDateString(), endValue);
                }
                return '';
            },
        },
        xAxis: {
            type: 'time',
            min: paddedStart,
            max: paddedEnd,
            axisLine: {
                show: true,
            },
        },
        yAxis: {
            type: 'category',
            data: categories,
            axisLabel: {
                show: showY_axis ? true : false,
                fontSize: 10,
                formatter: function (value) {
                    return value.split('\n').join('\n');
                },
            },
            axisLine: {
                show: showY_axis ? true : false,
            },
            axisTick: {
                show: showY_axis ? true : false,
            },
        },
        series: [
            {
                type: 'custom',
                renderItem: renderObjectiveItem,
                data: [objectivesData],
                encode: {
                    x: [1, 3],
                    y: 0,
                },
            },
            {
                type: 'custom',
                renderItem: renderSampleItem,
                data: data,
                barWidth: 30,
                encode: {
                    x: [1, 2],
                    y: 0,
                },
            },
            {
                type: 'custom',
                renderItem: renderLastSampleItem,
                data: [lastSampleData],
                barWidth: 30,
                encode: {
                    x: [1, 2],
                    y: 0,
                },
            },
            {
                type: 'custom',
                renderItem: renderArrowItem,
                data: data,
                encode: {
                    x: [1, 2],
                    y: 0,
                },
            },
            missedDateData && {
                type: 'line',
                data: missedDateData.data,
                lineStyle: {
                    type: 'dotted',
                    color: 'rgba(255, 0, 0, 0.466)',
                },
                itemStyle: {
                    color: 'red',
                },
            },
        ],
    };

    const handleChangeShowY_axis = (value) => {
        setShowY_axis(value);
    };

    return (
        <div className='qualitative-chart-container'>
            <B2BChart
                options={options}
                enableZoom={true}
                enableShowToday={true}
                enableShowObjectives={true}
                className='qualitative-chart'
            />
            <BooleanIconToggle
                value={showY_axis}
                onChange={handleChangeShowY_axis}
                trueIcon={<VisibilityOff />}
                trueText={'Hide Y-axis'}
                falseIcon={<Visibility />}
                falseText={'Show Y-axis'}
                className='qualitative-chart-y-axis-toggle'
            />
        </div>
    );
}

export default QualitativeChart;